import { useCallback, useEffect, useMemo, useState } from 'react'

export type TableColumnDef = {
  id: string
  label: string
  /** Always visible; cannot be hidden. */
  required?: boolean
  /** Keep column fixed at the start or end when reordering. */
  pin?: 'start' | 'end'
  /**
   * Structural chrome (checkbox, expand, actions) — keep in the table,
   * but omit from the «Столбцы» settings list.
   */
  hideInSettings?: boolean
  /**
   * Structural column with fixed width — do not show resize handle.
   */
  hideResize?: boolean
  /** Default visibility when no stored prefs. Defaults to true. */
  defaultVisible?: boolean
  defaultWidth?: number
  minWidth?: number
}

export type TableColumnPrefsState = {
  order: string[]
  hidden: string[]
  widths: Record<string, number>
}

const storageKey = (tableId: string) => `market_table_cols:${tableId}`

const defaultHidden = (columns: TableColumnDef[]) =>
  columns.filter((column) => column.defaultVisible === false && !column.required).map((column) => column.id)

const defaultWidths = (columns: TableColumnDef[]) => {
  const widths: Record<string, number> = {}
  for (const column of columns) {
    if (column.defaultWidth != null) {
      widths[column.id] = column.defaultWidth
    }
  }
  return widths
}

const normalizePrefs = (
  columns: TableColumnDef[],
  stored: Partial<TableColumnPrefsState> | null,
): TableColumnPrefsState => {
  const ids = new Set(columns.map((column) => column.id))
  const required = new Set(columns.filter((column) => column.required).map((column) => column.id))
  const pinStart = columns.filter((column) => column.pin === 'start').map((column) => column.id)
  const pinEnd = columns.filter((column) => column.pin === 'end').map((column) => column.id)
  const pinned = new Set([...pinStart, ...pinEnd])
  const baseMiddle = columns.filter((column) => !pinned.has(column.id)).map((column) => column.id)

  const orderFromStore = Array.isArray(stored?.order)
    ? stored!.order.filter((id): id is string => typeof id === 'string' && ids.has(id) && !pinned.has(id))
    : []
  const middle = [...orderFromStore, ...baseMiddle.filter((id) => !orderFromStore.includes(id))]
  const order = [...pinStart, ...middle, ...pinEnd]

  const hiddenFromStore = Array.isArray(stored?.hidden)
    ? stored!.hidden.filter((id): id is string => typeof id === 'string' && ids.has(id) && !required.has(id))
    : defaultHidden(columns)

  // Columns newly added to the app after prefs were saved should still honor defaultVisible:false.
  const knownFromStore = new Set<string>([
    ...(Array.isArray(stored?.order) ? stored.order.filter((id): id is string => typeof id === 'string') : []),
    ...(Array.isArray(stored?.hidden) ? stored.hidden.filter((id): id is string => typeof id === 'string') : []),
  ])
  const hidden = new Set(hiddenFromStore)
  if (stored != null && (Array.isArray(stored.order) || Array.isArray(stored.hidden))) {
    for (const column of columns) {
      if (column.defaultVisible === false && !column.required && !knownFromStore.has(column.id)) {
        hidden.add(column.id)
      }
    }
  }

  const widths: Record<string, number> = { ...defaultWidths(columns) }
  if (stored?.widths && typeof stored.widths === 'object') {
    for (const [id, width] of Object.entries(stored.widths)) {
      if (ids.has(id) && typeof width === 'number' && Number.isFinite(width) && width > 0) {
        widths[id] = width
      }
    }
  }

  return { order, hidden: [...hidden], widths }
}

const readStored = (tableId: string, columns: TableColumnDef[]): TableColumnPrefsState => {
  try {
    const raw = localStorage.getItem(storageKey(tableId))
    if (!raw) {
      return normalizePrefs(columns, null)
    }
    return normalizePrefs(columns, JSON.parse(raw) as Partial<TableColumnPrefsState>)
  } catch {
    return normalizePrefs(columns, null)
  }
}

const writeStored = (tableId: string, prefs: TableColumnPrefsState) => {
  try {
    localStorage.setItem(storageKey(tableId), JSON.stringify(prefs))
  } catch {
    // ignore quota / private mode
  }
}

export const useTableColumnPrefs = (tableId: string, columns: TableColumnDef[]) => {
  const columnsKey = columns.map((column) => column.id).join('|')
  const [prefs, setPrefs] = useState<TableColumnPrefsState>(() => readStored(tableId, columns))

  useEffect(() => {
    setPrefs(readStored(tableId, columns))
    // columns identity tracked via columnsKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId, columnsKey])

  const columnById = useMemo(() => {
    const map = new Map<string, TableColumnDef>()
    for (const column of columns) {
      map.set(column.id, column)
    }
    return map
  }, [columns])

  const commit = useCallback(
    (updater: (current: TableColumnPrefsState) => TableColumnPrefsState) => {
      setPrefs((current) => {
        const next = updater(current)
        writeStored(tableId, next)
        return next
      })
    },
    [tableId],
  )

  const visibleColumns = useMemo(() => {
    const hidden = new Set(prefs.hidden)
    return prefs.order
      .map((id) => columnById.get(id))
      .filter((column): column is TableColumnDef => column != null && !hidden.has(column.id))
  }, [columnById, prefs.hidden, prefs.order])

  const orderedColumns = useMemo(() => {
    return prefs.order
      .map((id) => columnById.get(id))
      .filter((column): column is TableColumnDef => column != null)
  }, [columnById, prefs.order])

  const settingsColumns = useMemo(
    () => orderedColumns.filter((column) => !column.hideInSettings),
    [orderedColumns],
  )

  const isVisible = useCallback(
    (id: string) => {
      const column = columnById.get(id)
      if (!column) {
        return false
      }
      if (column.required) {
        return true
      }
      return !prefs.hidden.includes(id)
    },
    [columnById, prefs.hidden],
  )

  const getWidth = useCallback(
    (id: string) => {
      const column = columnById.get(id)
      return prefs.widths[id] ?? column?.defaultWidth
    },
    [columnById, prefs.widths],
  )

  const getMinWidth = useCallback(
    (id: string) => columnById.get(id)?.minWidth ?? 56,
    [columnById],
  )

  const setWidth = useCallback(
    (id: string, width: number) => {
      const minWidth = getMinWidth(id)
      const nextWidth = Math.max(minWidth, Math.round(width))
      commit((current) => ({
        ...current,
        widths: { ...current.widths, [id]: nextWidth },
      }))
    },
    [commit, getMinWidth],
  )

  const setColumnVisible = useCallback(
    (id: string, visible: boolean) => {
      const column = columnById.get(id)
      if (!column || column.required) {
        return
      }
      commit((current) => {
        const hidden = new Set(current.hidden)
        if (visible) {
          hidden.delete(id)
        } else {
          hidden.add(id)
        }
        return { ...current, hidden: [...hidden] }
      })
    },
    [columnById, commit],
  )

  const moveColumn = useCallback(
    (id: string, direction: -1 | 1) => {
      const column = columnById.get(id)
      if (!column || column.pin) {
        return
      }
      commit((current) => {
        const order = [...current.order]
        const index = order.indexOf(id)
        if (index < 0) {
          return current
        }
        let target = index + direction
        while (target >= 0 && target < order.length) {
          const targetColumn = columnById.get(order[target]!)
          if (targetColumn && !targetColumn.pin) {
            ;[order[index], order[target]] = [order[target]!, order[index]!]
            return { ...current, order }
          }
          target += direction
        }
        return current
      })
    },
    [columnById, commit],
  )

  /** Place `dragId` at the position of `dropId` (left→right table order). */
  const reorderColumn = useCallback(
    (dragId: string, dropId: string) => {
      if (dragId === dropId) {
        return
      }
      const dragColumn = columnById.get(dragId)
      const dropColumn = columnById.get(dropId)
      if (!dragColumn || !dropColumn || dragColumn.pin || dropColumn.pin) {
        return
      }
      commit((current) => {
        const order = [...current.order]
        const from = order.indexOf(dragId)
        const to = order.indexOf(dropId)
        if (from < 0 || to < 0) {
          return current
        }
        order.splice(from, 1)
        const insertAt = order.indexOf(dropId)
        if (insertAt < 0) {
          return current
        }
        order.splice(insertAt, 0, dragId)
        return { ...current, order }
      })
    },
    [columnById, commit],
  )

  const resetPrefs = useCallback(() => {
    const next = normalizePrefs(columns, null)
    writeStored(tableId, next)
    setPrefs(next)
  }, [columns, tableId])

  return {
    prefs,
    visibleColumns,
    orderedColumns,
    settingsColumns,
    visibleCount: visibleColumns.length,
    isVisible,
    getWidth,
    getMinWidth,
    setWidth,
    setColumnVisible,
    moveColumn,
    reorderColumn,
    resetPrefs,
  }
}

export type UseTableColumnPrefsResult = ReturnType<typeof useTableColumnPrefs>
